import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { BehaviorSubject, catchError, map, Observable, Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';

import { LanguageService } from '@fcom/ui-translate';
import { unsubscribe } from '@fcom/core/utils';
import { ButtonTheme, NotificationTheme } from '@fcom/ui-components';
import { DatepickerTheme } from '@fcom/service-forms/components/common/datepicker.component';
import { AppState } from '@fcom/core';
import { profile } from '@fcom/core/selectors';
import { CorporateUserRole, Profile } from '@fcom/core-api/login';
import { finShare } from '@fcom/rx/operators/finShare';
import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import {
  CorporateSalesForceUser,
  CorporateUserForm,
  PassportDetailsForm,
  PersonalDetailsForm,
  UserDetailsForm,
} from '@fcom/common/interfaces';

import { parsePhoneNumber } from '../../utils/utils';

@Component({
  selector: 'fin-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss'],
})
export class AddUserComponent implements OnInit, OnDestroy {
  protected readonly ButtonTheme = ButtonTheme;
  protected readonly NotificationTheme = NotificationTheme;
  protected readonly DatepickerTheme = DatepickerTheme;

  formGroup: FormGroup<CorporateUserForm>;
  personalDetailsForm: FormGroup<PersonalDetailsForm>;
  passportDetailsForm: FormGroup<PassportDetailsForm>;
  userDetailsForm: FormGroup<UserDetailsForm>;

  posting = false;
  profile$: Observable<Profile>;
  errorNotification$: Subject<boolean> = new BehaviorSubject(false);

  isAdmin$: Observable<boolean>;
  isTravelBooker$: Observable<boolean>;

  subscriptions = new Subscription();

  constructor(
    private store$: Store<AppState>,
    private router: Router,
    private corporateService: CorporateService,
    private languageService: LanguageService,
    private elementRef: ElementRef
  ) {}
  ngOnInit(): void {
    this.profile$ = this.store$.pipe(profile(), finShare());
    this.isAdmin$ = this.profile$.pipe(map((p) => p?.corporate?.userRole === CorporateUserRole.ADMIN));
    this.isTravelBooker$ = this.profile$.pipe(map((p) => p?.corporate?.userRole === CorporateUserRole.TRAVEL_BOOKER));
    this.formGroup = this.corporateService.getCorporateUserForm();
    this.personalDetailsForm = this.formGroup.get('personalDetails') as FormGroup<PersonalDetailsForm>;
    this.passportDetailsForm = this.formGroup.get('passportDetails') as FormGroup<PassportDetailsForm>;
    this.userDetailsForm = this.formGroup.get('userDetails') as FormGroup<UserDetailsForm>;
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  navigateBack(): void {
    this.router.navigate([this.languageService.langValue, 'corporate', 'users']);
  }

  submit(): void {
    this.errorNotification$.next(false);

    if (!this.formGroup.valid) {
      this.corporateService.scrollToInvalidField(this.formGroup, this.elementRef);
      return;
    }

    this.posting = true;

    this.subscriptions.add(
      this.corporateService
        .addUser(this.createPostPayload())
        .pipe(
          catchError((err: unknown) => {
            this.posting = false;
            this.errorNotification$.next(true);
            throw err;
          })
        )
        .subscribe(() => {
          this.posting = false;
          this.navigateBack();
        })
    );
  }

  private createPostPayload(): CorporateSalesForceUser {
    return {
      allowEmails: true,
      birthdate: this.personalDetailsForm.get('dateOfBirth').value?.toString(),
      expires: this.passportDetailsForm.get('expirationDate').value?.toString(),
      firstname: this.personalDetailsForm.get('firstName').value,
      fplusNumber: this.personalDetailsForm.get('fPlusNumber').value,
      issued: this.passportDetailsForm.get('issueDate').value?.toString(),
      jobTitle: this.personalDetailsForm.get('workTitle').value,
      lastname: this.personalDetailsForm.get('lastName').value,
      mobileNumber: parsePhoneNumber(
        this.personalDetailsForm.get('phonePrefix').value,
        this.personalDetailsForm.get('phoneNumber').value
      ),
      nationality: this.passportDetailsForm.get('nationality').value,
      passportNumber: this.passportDetailsForm.get('passportNumber').value,
      role: this.userDetailsForm.get('role').value,
      title: this.personalDetailsForm.get('salutation').value,
      travelingClass: 'business',
      upEmail: this.personalDetailsForm.get('email').value,
    };
  }
}
